import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"

export default class extends Controller {
  static targets = ["canvas", "fallback"]
  static values = {
    coverUrl: String,
    spineUrl: String,
    backUrl: String,
    modelType: { type: String, default: "standard_book" },
    spineColor: { type: String, default: "#2d1810" },
    pageColor: { type: String, default: "#f5f5dc" },
    interactive: { type: Boolean, default: true },
    autoRotate: { type: Boolean, default: false },
    modelPath: String,
    title: { type: String, default: "" },
    backBlurb: { type: String, default: "" }
  }

  connect() {
    if (!this.hasCanvasTarget) return

    if (!this.isWebGLAvailable()) {
      this.showFallback()
      return
    }

    this.setupIntersectionObserver()
  }

  disconnect() {
    this.cleanup()
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.initialized) {
          this.initScene()
          this.observer.disconnect()
        }
      })
    }, { threshold: 0.1 })

    this.observer.observe(this.element)
  }

  isWebGLAvailable() {
    try {
      const canvas = document.createElement("canvas")
      return !!(window.WebGLRenderingContext &&
        (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")))
    } catch (e) {
      return false
    }
  }

  showFallback() {
    if (this.hasCanvasTarget) {
      this.canvasTarget.classList.add("hidden")
    }
    if (this.hasFallbackTarget) {
      this.fallbackTarget.classList.remove("hidden")
    }
  }

  initScene() {
    if (this.initialized) return
    this.initialized = true

    const canvas = this.canvasTarget
    const container = canvas.parentElement
    const width = container.clientWidth
    const height = container.clientHeight || 300

    this.scene = new THREE.Scene()
    this.clock = new THREE.Clock()
    this.time = 0

    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000)
    this.camera.position.set(0, 0, 3)

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true
    })
    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.outputColorSpace = THREE.SRGBColorSpace

    this.addLights()

    if (this.modelPathValue) {
      this.loadGLBModel()
    } else {
      this.createProceduralBook()
    }

    if (this.interactiveValue) {
      this.setupControls()
    }

    this.animate()
    this.setupResizeObserver()
  }

  addLights() {
    // Brighter warm ambient light (matches homepage)
    const ambient = new THREE.AmbientLight(0xfff8f0, 1.2)
    this.scene.add(ambient)

    // Main key light - warm and bright
    this.keyLight = new THREE.DirectionalLight(0xffeedd, 1.2)
    this.keyLight.position.set(3, 4, 5)
    this.scene.add(this.keyLight)

    // Shimmer light for dynamic highlights
    this.shimmerLight = new THREE.PointLight(0xffd700, 0.5, 12)
    this.shimmerLight.position.set(0, 0, 4)
    this.scene.add(this.shimmerLight)

    // Warm fill light from the side
    const fill = new THREE.DirectionalLight(0xffe4c4, 0.6)
    fill.position.set(-4, 2, 3)
    this.scene.add(fill)

    // Warm back light
    const rim = new THREE.DirectionalLight(0xd4b896, 0.4)
    rim.position.set(0, 0, -5)
    this.scene.add(rim)

    // Additional front fill for brightness
    const frontFill = new THREE.DirectionalLight(0xfff5e6, 0.5)
    frontFill.position.set(0, 0, 6)
    this.scene.add(frontFill)
  }

  createProceduralBook() {
    const dimensions = this.getBookDimensions()

    const geometry = new THREE.BoxGeometry(
      dimensions.width,
      dimensions.height,
      dimensions.depth
    )

    const materials = this.createBookMaterials(dimensions)
    this.book = new THREE.Mesh(geometry, materials)

    this.book.rotation.y = -0.3
    this.scene.add(this.book)
  }

  getBookDimensions() {
    const dimensions = {
      standard_book: { width: 1, height: 1.5, depth: 0.15 },
      thin_paperback: { width: 0.9, height: 1.35, depth: 0.08 },
      thick_hardcover: { width: 1.1, height: 1.6, depth: 0.25 },
      magazine: { width: 1.2, height: 1.5, depth: 0.03 }
    }
    return dimensions[this.modelTypeValue] || dimensions.standard_book
  }

  createBookMaterials(dimensions) {
    const textureLoader = new THREE.TextureLoader()
    const materials = []

    const spineColor = new THREE.Color(this.spineColorValue)
    const pageColor = new THREE.Color(this.pageColorValue)

    // Right side (pages) - warm page edges
    materials.push(new THREE.MeshStandardMaterial({
      color: pageColor,
      roughness: 0.7,
      emissive: 0x3d2d1f,
      emissiveIntensity: 0.05
    }))

    // Left side (spine) - with text
    materials.push(this.createSpineMaterial(textureLoader, spineColor))

    // Top pages
    materials.push(new THREE.MeshStandardMaterial({
      color: pageColor,
      roughness: 0.7,
      emissive: 0x3d2d1f,
      emissiveIntensity: 0.05
    }))

    // Bottom pages
    materials.push(new THREE.MeshStandardMaterial({
      color: pageColor,
      roughness: 0.7,
      emissive: 0x3d2d1f,
      emissiveIntensity: 0.05
    }))

    // Front cover
    if (this.coverUrlValue) {
      const coverTexture = textureLoader.load(this.coverUrlValue)
      coverTexture.colorSpace = THREE.SRGBColorSpace
      materials.push(new THREE.MeshStandardMaterial({
        map: coverTexture,
        color: 0xfff8f0,
        roughness: 0.2,
        metalness: 0.08,
        emissive: 0x3d2d1f,
        emissiveIntensity: 0.15
      }))
    } else {
      materials.push(new THREE.MeshStandardMaterial({
        color: spineColor,
        roughness: 0.4,
        metalness: 0.1,
        emissive: 0x2d1f14,
        emissiveIntensity: 0.1
      }))
    }

    // Back cover - with blurb text or image
    if (this.backUrlValue) {
      const backTexture = textureLoader.load(this.backUrlValue)
      backTexture.colorSpace = THREE.SRGBColorSpace
      materials.push(new THREE.MeshStandardMaterial({
        map: backTexture,
        roughness: 0.35,
        metalness: 0.05,
        emissive: 0x2d1f14,
        emissiveIntensity: 0.1
      }))
    } else {
      // Create back cover with blurb text
      const backCanvas = this.createBackTexture(spineColor)
      const backTexture = new THREE.CanvasTexture(backCanvas)
      materials.push(new THREE.MeshStandardMaterial({
        map: backTexture,
        roughness: 0.35,
        metalness: 0.05,
        emissive: 0x2d1f14,
        emissiveIntensity: 0.1
      }))
    }

    return materials
  }

  createSpineMaterial(textureLoader, spineColor) {
    if (this.spineUrlValue) {
      const spineTexture = textureLoader.load(this.spineUrlValue)
      spineTexture.colorSpace = THREE.SRGBColorSpace
      return new THREE.MeshStandardMaterial({
        map: spineTexture,
        roughness: 0.4,
        metalness: 0.1,
        emissive: 0x2d1f14,
        emissiveIntensity: 0.1
      })
    }

    // Create spine with title text
    const spineCanvas = this.createSpineTexture(spineColor)
    const spineTexture = new THREE.CanvasTexture(spineCanvas)
    return new THREE.MeshStandardMaterial({
      map: spineTexture,
      roughness: 0.4,
      metalness: 0.1,
      emissive: 0x2d1f14,
      emissiveIntensity: 0.1
    })
  }

  createSpineTexture(baseColor) {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 512
    const ctx = canvas.getContext('2d')

    // Background color
    ctx.fillStyle = `rgb(${Math.floor(baseColor.r*255)},${Math.floor(baseColor.g*255)},${Math.floor(baseColor.b*255)})`
    ctx.fillRect(0, 0, 64, 512)

    // Title text (rotated)
    const title = this.titleValue || ''
    if (title) {
      ctx.save()
      ctx.translate(32, 256)
      ctx.rotate(-Math.PI / 2)
      ctx.fillStyle = '#d4c4a8'
      ctx.font = 'bold 28px Georgia, serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      // Adjust font size to fit
      const maxWidth = 480
      let fontSize = 28
      ctx.font = `bold ${fontSize}px Georgia, serif`
      while (ctx.measureText(title.toUpperCase()).width > maxWidth && fontSize > 12) {
        fontSize -= 2
        ctx.font = `bold ${fontSize}px Georgia, serif`
      }

      ctx.fillText(title.toUpperCase(), 0, 0)
      ctx.restore()
    }

    return canvas
  }

  createBackTexture(baseColor) {
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 384
    const ctx = canvas.getContext('2d')

    // Slightly darker background
    const r = Math.floor(baseColor.r * 255 * 0.9)
    const g = Math.floor(baseColor.g * 255 * 0.9)
    const b = Math.floor(baseColor.b * 255 * 0.9)
    ctx.fillStyle = `rgb(${r},${g},${b})`
    ctx.fillRect(0, 0, 256, 384)

    // Blurb text
    const blurb = this.backBlurbValue || ''
    if (blurb) {
      ctx.fillStyle = '#d4c4a8'
      ctx.font = '18px Georgia, serif'
      ctx.textAlign = 'center'

      // Word wrap the text
      const words = blurb.split(' ')
      const lines = []
      let currentLine = ''
      const maxWidth = 220

      words.forEach(word => {
        const testLine = currentLine + (currentLine ? ' ' : '') + word
        if (ctx.measureText(testLine).width > maxWidth) {
          lines.push(currentLine)
          currentLine = word
        } else {
          currentLine = testLine
        }
      })
      if (currentLine) lines.push(currentLine)

      const lineHeight = 26
      const startY = (384 - lines.length * lineHeight) / 2

      lines.forEach((line, i) => {
        ctx.fillText(line, 128, startY + i * lineHeight)
      })
    }

    return canvas
  }

  loadGLBModel() {
    const loader = new GLTFLoader()

    loader.load(
      this.modelPathValue,
      (gltf) => {
        this.book = gltf.scene
        this.applyTexturesToModel()
        this.scene.add(this.book)
      },
      undefined,
      (error) => {
        console.warn("Failed to load GLB model, falling back to procedural:", error)
        this.createProceduralBook()
      }
    )
  }

  applyTexturesToModel() {
    if (!this.book) return

    const textureLoader = new THREE.TextureLoader()

    this.book.traverse((child) => {
      if (child.isMesh) {
        if (child.name.toLowerCase().includes("cover") && this.coverUrlValue) {
          const coverTexture = textureLoader.load(this.coverUrlValue)
          coverTexture.colorSpace = THREE.SRGBColorSpace
          child.material = new THREE.MeshStandardMaterial({
            map: coverTexture,
            color: 0xfff8f0,
            roughness: 0.2,
            metalness: 0.08,
            emissive: 0x3d2d1f,
            emissiveIntensity: 0.15
          })
        } else if (child.name.toLowerCase().includes("spine")) {
          if (this.spineUrlValue) {
            const spineTexture = textureLoader.load(this.spineUrlValue)
            spineTexture.colorSpace = THREE.SRGBColorSpace
            child.material = new THREE.MeshStandardMaterial({
              map: spineTexture,
              roughness: 0.4,
              metalness: 0.1,
              emissive: 0x2d1f14,
              emissiveIntensity: 0.1
            })
          } else {
            const spineCanvas = this.createSpineTexture(new THREE.Color(this.spineColorValue))
            const spineTexture = new THREE.CanvasTexture(spineCanvas)
            child.material = new THREE.MeshStandardMaterial({
              map: spineTexture,
              roughness: 0.4,
              metalness: 0.1,
              emissive: 0x2d1f14,
              emissiveIntensity: 0.1
            })
          }
        } else if (child.name.toLowerCase().includes("back")) {
          if (this.backUrlValue) {
            const backTexture = textureLoader.load(this.backUrlValue)
            backTexture.colorSpace = THREE.SRGBColorSpace
            child.material = new THREE.MeshStandardMaterial({
              map: backTexture,
              roughness: 0.35,
              metalness: 0.05,
              emissive: 0x2d1f14,
              emissiveIntensity: 0.1
            })
          } else {
            const backCanvas = this.createBackTexture(new THREE.Color(this.spineColorValue))
            const backTexture = new THREE.CanvasTexture(backCanvas)
            child.material = new THREE.MeshStandardMaterial({
              map: backTexture,
              roughness: 0.35,
              metalness: 0.05,
              emissive: 0x2d1f14,
              emissiveIntensity: 0.1
            })
          }
        } else if (child.name.toLowerCase().includes("page")) {
          child.material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(this.pageColorValue),
            roughness: 0.7,
            emissive: 0x3d2d1f,
            emissiveIntensity: 0.05
          })
        }
      }
    })
  }

  setupControls() {
    this.controls = new OrbitControls(this.camera, this.canvasTarget)
    this.controls.enableDamping = true
    this.controls.dampingFactor = 0.05
    this.controls.enableZoom = true
    this.controls.minDistance = 2
    this.controls.maxDistance = 6
    this.controls.autoRotate = this.autoRotateValue
    this.controls.autoRotateSpeed = 1.0

    if ('ontouchstart' in window) {
      this.controls.enableZoom = false
    }
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize()
    })
    this.resizeObserver.observe(this.canvasTarget.parentElement)
  }

  handleResize() {
    if (!this.renderer || !this.camera) return

    const container = this.canvasTarget.parentElement
    const width = container.clientWidth
    const height = container.clientHeight || 300

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
  }

  animate() {
    if (!this.renderer) return

    this.animationId = requestAnimationFrame(() => this.animate())

    const delta = this.clock.getDelta()
    this.time += delta

    // Animate shimmer light for dynamic highlights (slower for subtle effect)
    if (this.shimmerLight) {
      this.shimmerLight.position.x = Math.sin(this.time * 0.4) * 3
      this.shimmerLight.position.y = Math.cos(this.time * 0.3) * 2
      this.shimmerLight.intensity = 0.3 + Math.sin(this.time * 0.5) * 0.1
    }

    if (this.controls) {
      this.controls.update()
    }

    if (this.autoRotateValue && this.book && !this.controls) {
      this.book.rotation.y += 0.005
    }

    this.renderer.render(this.scene, this.camera)
  }

  cleanup() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }

    if (this.observer) {
      this.observer.disconnect()
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this.controls) {
      this.controls.dispose()
    }

    if (this.renderer) {
      this.renderer.dispose()
    }

    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose()
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose())
          } else {
            object.material.dispose()
          }
        }
      })
    }
  }
}
